import React, { useState, useEffect, useContext, useCallback } from 'react'

import { summaryIconsMap } from 'utils/mapping'

import { toast } from 'react-toastify'
import moment from 'moment'
import { UserContext } from './UserStore'

import useJobApi from 'api/jobApi'

import { exportAsExcelFile } from 'utils/excel'
import { useTranslation } from 'react-i18next'

import * as momentTimezone from 'moment-timezone'

const initialState = {
    jobId: '',
    highlightData: [],
    jobPipes: [],
    chartData: [],
    chartStoppagesData: [],
    jobInfo: [],
    performanceIndicators: [],
    stoppages: [],
    pipesSummary: [],
    accessoriesSummary: [],
    watchdogs: [],
    pipeListDonutChartData: []
}

export const JobContext = React.createContext(initialState)

export const JobProvider = ({ children }) => {
    const { idToken } = useContext(UserContext)
    const { t } = useTranslation()
    const jobApi = useJobApi(idToken)

    const [userJobs, setUserJobs] = useState([])
    const [jobId, setJobId] = useState('')

    const [highlightData, setHighlightData] = useState([])
    const [jobPipes, setJobPipes] = useState([])
    const [chartData, setChartData] = useState([])
    const [chartStoppagesData, setChartStoppagesData] = useState([])
    const [pipeListDonutChartData, setPipeListDonutChartData] = useState([])
    const [jobDetailsData, setJobDetailsData] = useState([])
    const [performanceIndicators, setPerformanceIndicators] = useState([])
    const [stoppages, setStoppages] = useState([])
    const [pipesSummary, setPipesSummary] = useState([])
    const [accessoriesSummary, setAccessoriesSummary] = useState([])
    const [watchdogs, setWatchdogs] = useState([])
    const [isJobsLoading, setIsJobsLoading] = useState(false)
    const [isJobDetailsLoading, setIsJobDetailsLoading] = useState(false)
    const [jobDataGlobal, setJobDataGlobal] = useState({})

    const [chartTimezone, setChartTimezone] = useState('UTC')

    const [isExcelLoading, setIsExcelLoading] = useState(false)
    const { registryLog, registryLogOld } = useContext(UserContext)

    const [pipeData, setPipeData] = useState([])

    const updateChartTimezone = (timezone) => {
        setChartTimezone(timezone)
        updateChartTimezoneItens(jobDataGlobal, timezone)
    }

    const updateChartTimezoneItens = (data, timezone) => {
        if (data && data.items) {
            const chartData = getChartData(data.items)
            const chartDataWithTimezone = updateChartItemsTimezone(chartData, timezone)
            setChartData(chartDataWithTimezone)
        }

        if (data && data.stoppages) {
            const stoppageChartdata = getChartStoppages(data.stoppages)
            const stoppagesChartDataWithTimezone = updateChartItemsTimezone(stoppageChartdata, timezone)
            setChartStoppagesData(stoppagesChartDataWithTimezone)
        }
    }

    const updateChartItemsTimezone = (items, timezone) => {
        return items
            .map(item => ({ ...item, x: momentTimezone.utc(item.x).tz(timezone).format('YYYY-MM-DDTHH:mm:ss'), start_time: momentTimezone.utc(item.start_time).tz(timezone).format('YYYY-MM-DDTHH:mm:ss'), end_time: momentTimezone.utc(item.end_time).tz(timezone).format('YYYY-MM-DDTHH:mm:ss') }))
    }

    const formatExcelData = (data) => {
        const excelData = []
        const newData = data.map((pipe, index) => ({ ...pipe, run_in_hole_sequence: pipe.downhole_scan_date || pipe.scan_date ? index + 1 : null }))
        for (const pipe of newData) {
            excelData.push({
                material: pipe.material,
                cod_material: pipe.cod_material,
                tally_number: pipe.tally_number,
                tracking_number: pipe.tracking_number,
                tracking_number_dv: pipe.tracking_number_dv,
                effective_length: pipe.effective_length,
                overall_length: pipe.overall_length,
                type: pipe.type,
                status: pipe.status,
                location_status: pipe.location_status,
                delivered_scan_date: pipe.delivered_scan_date || pipe.receive_date,
                downhole_scan_date: pipe.downhole_scan_date || pipe.scan_date,
                connection: pipe.connection,
                comments: pipe.comments,
                barcode: pipe.barcode,
                make_up_loss: pipe.make_up_loss,
                work_order: pipe.work_order,
                wall_thickness: pipe.wall_thickness,
                product: pipe.product,
                running_compound: pipe.running_compound,
                planned_sequence_number: pipe.planned_sequence_number,
                reject_reason: pipe.reject_reason,
                internal_diameter: pipe.internal_diameter,
                internal_diameter_actual: pipe.internal_diameter_actual,
                outer_diameter: pipe.outer_diameter,
                thread: pipe.thread,
                weight: pipe.weight,
                drift: pipe.drift,
                stand_number: pipe.stand_number,
            })
        }
        return excelData
    }

    const exportAllDataToExcel = () => {
        setIsExcelLoading(true)
        if (jobPipes && jobPipes.length > 0) {
            const excelData = formatExcelData(jobPipes)
            exportAsExcelFile(excelData, 'RUN_IN_HOLE_DATA_' + moment().format('YYYY-MM-DD_HH-mm'))
        }
        registryLogOld('click_exportExcel', "running-expert/job-details")
        registryLog('job_details', "click", "export_excel")

        setIsExcelLoading(true)
    }

    const exportAllPipeDataToExcel = () => {
        setIsExcelLoading(true)
        if (pipeData && pipeData.length > 0) {
            const excelData = formatExcelData(pipeData)
            exportAsExcelFile(excelData, 'RUN_IN_HOLE_DATA_' + moment().format('YYYY-MM-DD_HH-mm'))
        }
        setIsExcelLoading(true)
    }

    const getJobDate = (date) => date ? moment(date).format('YYYY-MM-DD') : 'TBD'

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const formatJobs = (jobs) => {
        return jobs.map(job => {
            if (job.job_id) {
                return { ...job, job_date: getJobDate(job.job_date), countryFlag: job.country }
            }
            return job
        })
    }

    const formatJobSummary = (summary) => {
        return summary && summary.map(item => ({ ...item, icon: summaryIconsMap[item.id] }))
    }

    const getChartData = (items) => {
        return items
            .filter(item => item.downhole_scan_date || item.scan_date)
            .map(item => ({ x: item.downhole_scan_date || item.scan_date, y: Math.round(item.depth, 0), name: item.tally_number, date: item.downhole_scan_date || item.scan_date, comments: item.comments }))
    }

    const getChartStoppages = (stoppages) => {
        return stoppages
            .filter(item => item.start_time)
            .map(item => ({ x: item.start_time, y: 0, reason: item.reason, start_time: item.start_time, end_time: item.end_time, isbreak: item.isbreak }))
    }

    const getAmountFromSummary = (summary, prop) => {
        return summary
            .filter(item => item.id === prop)
            .map(item => item.amount)
            .reduce((prev, curr) => prev + curr)
    }

    const getPipeListDonutChartData = useCallback((summary) => {
        const deliveredAmount = getAmountFromSummary(summary, "delivered")
        const downholeAmount = getAmountFromSummary(summary, "downhole")
        const returnedAmount = getAmountFromSummary(summary, "returned")
        const rejectedAmount = getAmountFromSummary(summary, "rejected")
        const donutChartData = [
            [
                { "name": t("DELIVERED"), "value": deliveredAmount, "color": '#59CBE4' },
                { "name": "DIFFERENCE", "value": 0, "color": '#919098' }
            ],
            [

                { "name": t("DOWNHOLE"), "value": downholeAmount, "color": '#59E480' },
                { "name": "DIFFERENCE", "value": deliveredAmount - downholeAmount, "color": '#919098' }

            ],
            [

                { "name": t("REJECTED"), "value": rejectedAmount, "color": '#FB7171' },
                { "name": "DIFFERENCE", "value": deliveredAmount - rejectedAmount, "color": '#919098' }

            ],
            [
                { "name": t("RETURNED"), "value": returnedAmount, "color": '#FBCA4D' },
                { "name": "DIFFERENCE", "value": deliveredAmount - returnedAmount, "color": '#919098' }
            ]
        ]
        return donutChartData
    }, [t])

    const formatJobSummaryDetails = (details) => {
        details.forEach((detail) => {
            if (detail.id === 'date') {
                if (detail && detail.value) {
                    return detail.value = moment(detail.value).format('DD/MM/YYYY')
                }
            }
        })
    }

    const getCheckDigit = (trackingNumberDB) => {
        let checksum = 0
        const valueArray = trackingNumberDB.split('')

        for (let i = 0; i < valueArray.length; i++) {
            checksum = checksum + (valueArray[i] * (valueArray.length - i + 1))
        }
        const checkDigit = checksum % 10
        return checkDigit.toString()
    }

    const getIppn = (data) => {
        if (Array.isArray(data) && data.length > 0) {
            data.forEach(ippnObject => {
                if (ippnObject.tracking_number) {
                    ippnObject.tracking_number = parseFloat(ippnObject.tracking_number)
                }
                if (ippnObject.tracking_number_dv) {
                    if (ippnObject.tracking_number_dv.toString().length === 12) {
                        ippnObject.tracking_number_dv = `${ippnObject.tracking_number_dv}-${getCheckDigit(ippnObject.tracking_number_dv)}`
                    } else {
                        ippnObject.tracking_number_dv = parseFloat(ippnObject.tracking_number_dv)
                    }
                }
            })
        }
        return data // Retorna o array de ippnObjects atualizado
    }

    /** 
     * Effection for getting job details
     */
    useEffect(() => {
        const hydrate = async () => {
            try {
                setIsJobDetailsLoading(true)
                if (jobId) {
                    const jobData = await jobApi.getJobDataFromApi(jobId)
                    if (jobData && jobData.summary) {
                        const pipesSummaryFormated = formatJobSummary(jobData.summary.pipesSummary)
                        const accessoriesSummaryFormated = formatJobSummary(jobData.summary.accessoriesSummary)

                        // ----- chart data  ------
                        const chartDataFormated = getChartData(jobData.items)
                        const chartDataWithTimezone = updateChartItemsTimezone(chartDataFormated, chartTimezone)
                        const chartStoppagesDataFormated = getChartStoppages(jobData.stoppages)
                        const chartStoppagesDataWithTimezone = updateChartItemsTimezone(chartStoppagesDataFormated, chartTimezone)
                        setChartData(chartDataWithTimezone)
                        setChartStoppagesData(chartStoppagesDataWithTimezone)

                        // ----- donut data  ------ 
                        const pipeListDonutChartDataFormated = getPipeListDonutChartData([...pipesSummaryFormated, ...accessoriesSummaryFormated])
                        setPipeListDonutChartData(pipeListDonutChartDataFormated)

                        formatJobSummaryDetails(jobData.jobInfo.jobDetailsData)
                        setHighlightData(jobData.highlightData)
                        setJobPipes(getIppn(jobData.items))
                        setJobDetailsData(jobData.jobInfo.jobDetailsData)
                        setPerformanceIndicators(jobData.performanceIndicators)
                        setStoppages(jobData.stoppages)
                        setPipesSummary(pipesSummaryFormated)
                        setAccessoriesSummary(accessoriesSummaryFormated)
                        setWatchdogs(jobData.watchdogs)
                        setJobDataGlobal(jobData)
                    }
                }

            } catch (error) {
                throw new Error(error)
            } finally {
                setIsJobDetailsLoading(false)
            }
        }

        hydrate().catch(err => {
            toast.error(err.message)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId, getPipeListDonutChartData])


    /** 
     * Effection for getting jobs
     */
    useEffect(() => {
        const hydrate = async () => {
            try {
                setIsJobsLoading(true)
                const jobs = await jobApi.getJobsFromApi()
                if (jobs) {
                    const jobsFormated = formatJobs(jobs)
                    setUserJobs(jobsFormated)
                }
            } catch (error) {
                throw new Error(error)
            } finally {
                setIsJobsLoading(false)
            }
        }

        hydrate().catch(err => {
            toast.error(err.message)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const renderStore =
        <JobContext.Provider value={{
            userJobs,
            jobId,
            setJobId,
            highlightData,
            jobPipes,
            chartData,
            chartStoppagesData,
            pipeListDonutChartData,
            jobDetailsData,
            performanceIndicators,
            stoppages,
            pipesSummary,
            accessoriesSummary,
            watchdogs,
            isJobsLoading,
            isJobDetailsLoading,
            exportAllDataToExcel,
            isExcelLoading,
            chartTimezone,
            setChartTimezone,
            updateChartTimezone,
            setPipeData,
            exportAllPipeDataToExcel,
        }}>
            {children}
        </JobContext.Provider>

    return renderStore
}
